import { ClusterType } from '@/swagger/constants/credits-enums';

export const en = {
  departments: {
    pageTitle: 'Departments',
    list: {
      subTitle: 'Departments List',
      team: 'Team: ',
      manager: 'Manager: ',
      viewDetails: 'View details',
      fields: {
        searchPlaceholder: 'Search',
      },
      noResults: 'No departments found',
      noResultsInfo: 'For more information, check the !{CTA1}! or !{CTA2}!.',
      ctaLabel1: 'docuementation',
      ctaLabel2: 'contact us',
      noDepartmentName: 'No name',
      unassignedMembers: {
        title: 'Organization members without a department',
        description: 'Some members have not yet been assigned to a department',
        table: {
          searchPlaceholder: 'Search',
          firstName: 'First name',
          lastName: 'Last name',
          email: 'Email',
          role: 'Company role',
        },
      },
    },
    details: {
      departmentManager: 'Department Manager',
      manager: 'Manager: ',
      teamManager: 'Team Manager',
      noDepartmentName: 'No name',
      noTeamName: 'Department members',
      validProfiles: 'Valid profiles',
      profilesToCorrect: 'Profiles to correct',
      team: 'Team',
      goToPeople: 'Go to People',
      teamMembers: 'Team members',
      members: 'Members',
      tabs: {
        tab1: 'Department',
        team: 'Team',
        tab2: 'Assessment',
        tab3: 'Credits',
      },
      managerNotExist:
        'The manager’s details are not visible because the profile is not registered on the platform. Add the record to be able to view all the information.',
    },
    emptyStateNoPeople: {
      title: 'Departments',
      descriptionLine1: 'No complete profiles available.',
      descriptionLine2:
        'To view the data, you need to register the company profiles in the !{CTA}! section.',
      ctaLabel: 'Profiles',
    },
    emptyStateNoDepartments: {
      title: 'Departments',
      description:
        'The uploaded profiles do not yet contain department information.\n\nTo create departments and assign people, update the profiles from the !{CTA}! section. This will allow you to view more specific insights for each department and team.',
      ctaLabel: 'Profiles',
    },
    usefullInformations: {
      title: 'Useful materials',
      boxTutorial: {
        title: 'Watch the tutorial',
        description: 'Learn how to view and interpret your department results',
        buttonLabel: 'Watch now',
      },
    },
    tabs: {
      assessment: {
        noCompletedEmployees: {
          title: 'Assessment',
          description:
            'The uploaded profiles contain errors or incomplete fields. Check and correct them to send an assessment.',
          alert: {
            label: 'Go to the !{CTA}! section to proceed.',
            ctaLabel: 'Guide',
          },
        },
        noData: {
          title: 'Assessment',
          description:
            'You have not yet sent the assessment to any member. Click “Send Assessment” to start collecting skill results.',
          button: {
            label: 'Send Assessment',
          },
          alert: {
            label: 'To learn more about how Assessment works, check the !{CTA}!.',
            ctaLabel: 'Guide',
          },
        },
      },
      team: {
        assessment: {
          description: 'Select a questionnaire from the list below to view the results.',
          intro: {
            description: {
              single:
                'The data refers to team members who completed the assessment on “{{nameSurvey}}”. The analysis base consists of {{totalCompletedSurveys}} user.',
              plural:
                'The data refers to team members who completed the assessment on “{{nameSurvey}}”. The analysis base consists of {{totalCompletedSurveys}} users.',
            },
          },
          recap: {
            title: 'Average score per {{templateName}}',
            description:
              'The chart shows the average scores (scale 0-7) of the {{metricsCount}} {{templateName}} considered essential to effectively face AI challenges.',
            insignBox: {
              title: 'Insight',
              proTitle: 'Strengths',
              conTitle: 'Weaknesses',
            },
          },
          resultsTeamVsDepartmentVsOrganization: {
            title: 'Team performance compared to department and organization',
            description:
              'Comparison between the average scores of the team, its department, and the entire organization in the selected edition.',
            labelTooltipChart:
              'Score: {{current}} pt\n\n{{depValue}} vs department average\n\n{{orgValue}} vs organization average',
            legend: {
              teamAverage: 'Team ({{total}} people)',
              depAverage: 'Department ({{total}} people)',
              orgAverage: 'Organization ({{total}} people)',
            },
          },
          distributionBox: {
            title: 'Score distribution by skill',
            description:
              'The chart shows the percentage of team members placed in the three competence levels for each skill.',
          },
        },
      },
      department: {
        assessment: {
          description: 'Select a questionnaire from the list below to view the results.',
          intro: {
            description: {
              single:
                'The data refers to department members who completed the assessment on “{{nameSurvey}}”. The analysis base consists of {{totalCompletedSurveys}} user.',
              plural:
                'The data refers to department members who completed the assessment on “{{nameSurvey}}”. The analysis base consists of {{totalCompletedSurveys}} users.',
            },
          },
          recap: {
            title: 'Average score per {{templateName}}',
            description:
              'The chart shows the average scores (scale 0-7) of the {{metricsCount}} {{templateName}} considered essential to effectively face AI challenges.',
            insignBox: {
              title: 'Insight',
              proTitle: 'Strengths',
              conTitle: 'Weaknesses',
            },
          },
          resultsDepartmentVsOrganization: {
            title: 'Department performance compared to organization',
            description:
              'Comparison between the average scores of the department and the entire organization in the selected edition.',
            labelTooltipChart: 'Score: {{current}} pt\n\n{{diffValue}} vs organization average',
            legend: {
              depAverage: 'Department ({{total}} people)',
              orgAverage: 'Organization ({{total}} people)',
            },
          },
          distributionBox: {
            title: 'Score distribution by skill',
            description:
              'The chart shows the percentage of department members placed in the three competence levels for each skill.',
          },
        },
      },
      credits: {
        noData: {
          title: 'Credits',
          description:
            'No data available yet: send the assessment to start monitoring credit usage.',
        },
        recap: {
          title: 'Team {{name}}',
          nextRecharge: 'Credit renewal: !{DATE}! ({{days}} days remaining)',
          description:
            'The data considers department profiles invited to register on Slash (including users with pending accounts). Profiles uploaded but never sent the initial assessment are excluded.',
          descriptionTeam:
            'The data considers only team profiles invited to register on Slash (including users with pending accounts). Profiles uploaded but never sent the first assessment are excluded.',
        },
        progress: {
          total: 'Credits spent in the selected period: ',
          overall: 'Credit usage',
          overallTooltip:
            'The percentage indicates the share of credits used compared to the total allocation available for the department. Since credits accumulate for three months, actual monthly usage may exceed 100%, even though displayed values will never go beyond that threshold.',
          media: 'Average monthly usage: ',
        },
        cluster: {
          labelPerson: 'people in {{clusterName}}',
          title: 'Credit usage details',
          description:
            'Each range shows the average level of credit usage by department members. The distribution helps understand how many users fully use available credits and how many have room for engagement.',
          descriptionTeam:
            'Each range shows the average level of credit usage by team members. The distribution helps understand how many users fully use available credits and how many have room for engagement.',
          table: {
            cluster: 'Cluster',
            clusterTooltip:
              'Clusters are calculated based on total credit consumption in the selected period',
            media: 'Average credits',
            mediaLabel: 'credits/month',
            person: 'People',
            personLabel: 'people',
          },
          [ClusterType.INATTIVI]: {
            label: 'Inactive',
            subLabel: '0%',
          },
          [ClusterType.SOTTO_UTILIZZATORI]: {
            label: 'Under-users',
            subLabel: '1-29%',
          },
          [ClusterType.OCCASIONALI]: {
            label: 'Occasional',
            subLabel: '30-49%',
          },
          [ClusterType.MODERATI]: {
            label: 'Moderate',
            subLabel: '50-69%',
          },
          [ClusterType.REGOLARI]: {
            label: 'Regular',
            subLabel: '70-89%',
          },
          [ClusterType.INTENSIVI]: {
            label: 'Intensive',
            subLabel: '90-100%',
          },
        },
        team: {
          title: 'Credit usage by team',
          description: 'Details of credit usage for each team in the department.',
          buttonCta: 'View credits',
          noTeamName: 'No name',
          creditPerPerson: 'Average consumption per person: !{TOTAL}! credits',
        },
        trend: {
          title: 'Credit usage trend',
          description:
            'Analysis of average credit consumption in the department during the selected period.',
          descriptionTeam: 'Team consumption trend during the selected period.',
          chartTitle: 'Credit usage trend',
          averageMonthlyUsage: 'Average monthly usage',
          averageMonthlyUsageTooltip:
            'Indicates the average number of credits spent by each employee during the selected period. For quarterly periods, the value remains calculated on a monthly basis.',
          creditLabel: 'credits per person',
          creditsLabel: 'credits',
          trendLabel: 'Quarterly trend',
          trendLabelTooltip:
            'Shows the variation in average credit usage per person compared to the previous period of the same type (month or quarter). Helps identify growth or decline trends.',
        },
      },
    },
    tutorial: {
      title: 'Your departments',
      slides: {
        slide1: {
          title: 'Explore the department overview',
          description:
            'View the complete list of all departments in your organization. From here, you can select a specific department and access its dedicated page.',
        },
        slide2: {
          title: 'Check department information',
          description:
            'Each department has two tabs: “Department” with the people associated with the department and “Assessment” where you can monitor the team’s progress.',
        },
        slide3: {
          title: 'Dive into each team',
          description:
            'For each team in the department, the “Team” and “Assessment” tabs are available, allowing you to analyze information, performance, and resource usage in detail at a more granular level.',
        },
      },
    },
  },
};
